import { useEffect, useMemo, useState } from "react";
import { NavLink } from "react-router-dom";
import { ArrowLeftRight, Building2, Check, Search, Settings, X } from "lucide-react";
import { useRoutes } from "../../routes/RoutesProvider";
import { useAuth } from "../../providers/AuthProvider";
import { useTenant, type TenantOption } from "../../providers/TenantProvider";
import IndyLogo from "../../../assets/images/Indy-Logo.png";

const TenantSwitcherDialog = ({
  tenants,
  currentTenantId,
  onSelect,
  onClose,
}: {
  tenants: TenantOption[];
  currentTenantId: string;
  onSelect: (id: string) => void;
  onClose: () => void;
}) => {
  const [query, setQuery] = useState("");

  useEffect(() => {
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [onClose]);

  const filtered = useMemo(() => {
    const trimmed = query.trim().toLowerCase();
    if (!trimmed) return tenants;
    return tenants.filter((tenant) => tenant.name.toLowerCase().includes(trimmed));
  }, [tenants, query]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div
        className="flex max-h-[70vh] w-full max-w-md flex-col overflow-hidden rounded-xl border border-zinc-700 bg-zinc-900 shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-zinc-800 px-4 py-3">
          <h2 className="text-base font-semibold text-white">Switch Tenant</h2>
          <button onClick={onClose} className="rounded p-1 text-zinc-400 hover:bg-zinc-800 hover:text-white">
            <X size={18} />
          </button>
        </div>

        <div className="border-b border-zinc-800 p-3">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input
              type="text"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search tenants..."
              autoFocus
              className="w-full rounded-md border border-zinc-700 bg-zinc-800 py-2 pl-9 pr-3 text-sm text-white placeholder-zinc-500 focus:border-blue-500 focus:outline-none"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {filtered.length === 0 ? (
            <div className="p-6 text-center text-sm text-zinc-500">No tenants match "{query}"</div>
          ) : (
            <ul>
              {filtered.map((tenant) => {
                const isCurrent = tenant.id === currentTenantId;
                return (
                  <li key={tenant.id}>
                    <button
                      onClick={() => onSelect(tenant.id)}
                      className={`flex w-full items-center gap-3 px-4 py-3 text-left text-sm transition-colors hover:bg-zinc-800 ${
                        isCurrent ? "bg-zinc-800/60 text-white" : "text-zinc-200"
                      }`}
                    >
                      <Building2 size={16} className="flex-shrink-0 text-zinc-500" />
                      <span className="flex-1 truncate">{tenant.name}</span>
                      {isCurrent && <Check size={16} className="flex-shrink-0 text-blue-400" />}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="border-t border-zinc-800 px-4 py-2 text-xs text-zinc-500">
          {filtered.length} of {tenants.length} {tenants.length === 1 ? "tenant" : "tenants"}
        </div>
      </div>
    </div>
  );
};

const TenantBlock = () => {
  const { tenants, currentTenantId, setCurrentTenantId, hasMultipleTenants, loading } = useTenant();
  const [open, setOpen] = useState(false);

  if (loading || !currentTenantId) return null;
  const currentTenant = tenants.find((t) => t.id === currentTenantId);
  if (!currentTenant) return null;

  return (
    <>
      {hasMultipleTenants ? (
        <button
          onClick={() => setOpen(true)}
          title="Switch tenant"
          className="flex w-full items-center gap-2 rounded px-4 py-2 text-left text-sm text-white/80 transition-colors hover:bg-black/15"
        >
          <Building2 size={14} className="flex-shrink-0 opacity-80" />
          <span className="flex-1 truncate" title={currentTenant.name}>
            {currentTenant.name}
          </span>
          <ArrowLeftRight size={14} className="flex-shrink-0 opacity-80" />
        </button>
      ) : (
        <div className="flex items-center gap-2 px-4 py-2 text-sm text-white/80">
          <Building2 size={14} className="flex-shrink-0 opacity-80" />
          <span className="flex-1 truncate" title={currentTenant.name}>
            {currentTenant.name}
          </span>
        </div>
      )}
      {open && (
        <TenantSwitcherDialog
          tenants={tenants}
          currentTenantId={currentTenantId}
          onSelect={(id) => {
            setCurrentTenantId(id);
            setOpen(false);
          }}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
};

export const Sidebar = () => {
  const { groupedRoutes } = useRoutes();
  const { user, logout, hasPermission, permissions } = useAuth();
  const isPlatformSuperAdmin = permissions?.isPlatformSuperAdmin ?? false;

  // Filter routes based on user permissions
  const filteredGroupedRoutes = useMemo(() => {
    const filtered: Record<string, (typeof groupedRoutes)[string]> = {};

    for (const [section, routes] of Object.entries(groupedRoutes)) {
      const allowedRoutes = routes.filter((route) => {
        if (route.showInNavigation === false) return false;
        if (route.requirePlatformSuperAdmin && !isPlatformSuperAdmin) return false;
        // If no permission required, show the route
        if (!route.requiredPermission) return true;
        // Check if user has the required permission
        return hasPermission(route.requiredPermission);
      });

      // Only include section if it has routes
      if (allowedRoutes.length > 0) {
        filtered[section] = allowedRoutes;
      }
    }

    return filtered;
  }, [groupedRoutes, hasPermission, isPlatformSuperAdmin]);

  return (
    <aside className="w-64 bg-indy-orange text-white h-screen flex flex-col flex-shrink-0">
      <div className="p-6 flex-1 overflow-y-auto">
        {/* Logo */}
        <div className="mb-8 flex justify-center">
          <img src={IndyLogo} alt="Indy TV" className="h-16 w-auto" />
        </div>

        {/* Navigation */}
        <nav className="space-y-6">
          {Object.entries(filteredGroupedRoutes).map(([section, routes]) => (
            <div key={section}>
              <h3 className="px-4 text-xs font-semibold text-white/60 uppercase tracking-wider mb-2">{section}</h3>
              <ul className="space-y-1">
                {routes.map((route) => (
                  <li key={route.path}>
                    <NavLink
                      to={route.path}
                      className={({ isActive }) =>
                        `block px-4 py-2.5 rounded transition-colors font-medium ${
                          isActive ? "bg-indy-navy text-white" : "text-white hover:bg-black/15"
                        }`
                      }
                    >
                      {route.label}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>
      </div>

      {/* User & Logout */}
      <div className="p-4 border-t border-white/20">
        {user && (
          <NavLink
            to="/settings"
            className={({ isActive }) =>
              `flex items-center gap-2 px-4 py-2 text-sm rounded transition-colors ${
                isActive ? "bg-indy-navy text-white" : "text-white/80 hover:bg-black/15"
              }`
            }
          >
            <span className="flex-1 truncate">
              {user.firstName} {user.lastName}
            </span>
            <Settings size={14} className="flex-shrink-0 opacity-80" />
          </NavLink>
        )}
        <TenantBlock />
        <button
          onClick={logout}
          className="w-full px-4 py-2.5 text-left rounded transition-colors font-medium text-white hover:bg-black/15"
        >
          Logout
        </button>
      </div>
    </aside>
  );
};
